import { range } from 'lodash';
import { NumericRange, NumericScalingFunction } from '@ardoq/graph';
import {
  BUBBLE_LEGEND_STROKE_WIDTH,
  DATA_LABEL_LEFT_PADDING,
  LEGEND_TEXT_PX_SIZE,
  MAX_BUBBLE_RADIUS,
  MAX_INTERMEDIATE_STEPS,
  MIN_PIXELS_BETWEEN_BUBBLE_RADII,
  PADDING_BOTTOM_BUBBLES,
  PADDING_RIGHT_BUBBLES,
  PADDING_TOP_BUBBLES,
} from './consts';
import {
  BubbleChartSelectedFieldInfo,
  BubbleRadiusLegendProps,
  FieldData,
  LegendBubbleData,
  LegendBubbleDataRange,
  RadiusFieldLegendData,
} from '../types';
import { canvasLabelMeasurer } from '@ardoq/graph-labeling';
import { fontFamily } from '@ardoq/design-tokens';
import { formatNumber } from '@ardoq/locale';

const getBubbleData = (
  dataValue: number,
  radiusScaler: NumericScalingFunction
) => ({
  dataValue,
  radius: radiusScaler(dataValue),
});

const constrainLegendBubbleRange = (
  sortedDataVals: number[],
  radiusScaler: NumericScalingFunction,
  radiusUnscaler: NumericScalingFunction,
  maxBubbleRadius: number
): LegendBubbleDataRange => {
  const minBubbleDataVal = sortedDataVals[0];
  const minBubbleData = getBubbleData(minBubbleDataVal, radiusScaler);

  const minNonZeroDataVal = sortedDataVals.find(num => num !== 0);
  const minNonZeroBubbleData = minNonZeroDataVal
    ? getBubbleData(minNonZeroDataVal, radiusScaler)
    : null;

  const maxBubbleDataVal = sortedDataVals[sortedDataVals.length - 1];
  const maxBubbleData = getBubbleData(maxBubbleDataVal, radiusScaler);

  const constrainedMaxBubbleData =
    maxBubbleData.radius > maxBubbleRadius
      ? {
          dataValue: radiusUnscaler(maxBubbleRadius),
          radius: maxBubbleRadius,
        }
      : maxBubbleData;

  const isMinBubbleNonZeroAndLessThanMax =
    minBubbleData.radius > 0 &&
    minBubbleData.radius < constrainedMaxBubbleData.radius;
  if (isMinBubbleNonZeroAndLessThanMax)
    return [minBubbleData, constrainedMaxBubbleData];

  if (
    minNonZeroBubbleData &&
    minNonZeroBubbleData.radius < constrainedMaxBubbleData.radius
  )
    return [minNonZeroBubbleData, constrainedMaxBubbleData];

  return [null, constrainedMaxBubbleData];
};

/** Given min and max radii, equally space radii between them */
const getIntermediateRadiiSteps = (radiusRange: NumericRange) => {
  const [min, max] = radiusRange;
  const spaceForIntermediateSteps =
    max - min - 2 * MIN_PIXELS_BETWEEN_BUBBLE_RADII;
  if (spaceForIntermediateSteps < 0) return radiusRange;
  const numIntermediateSteps = Math.min(
    MAX_INTERMEDIATE_STEPS,
    Math.floor(spaceForIntermediateSteps / MIN_PIXELS_BETWEEN_BUBBLE_RADII) + 1
  );
  const spaceBetweenSteps = Math.floor(
    (max - min - MIN_PIXELS_BETWEEN_BUBBLE_RADII) / numIntermediateSteps
  );
  const intermediateSteps = range(1, numIntermediateSteps + 1).map(
    stepN => min + spaceBetweenSteps * stepN
  );
  return intermediateSteps;
};

/**
 * Return number of digits in the whole number part of a number
 * E.g. 10 -> 2, 1 -> 1, 123.45 -> 3, 0.1 -> 1
 */
const getNumWholeDigits = (n: number) => {
  const abs = Math.abs(n);
  if (abs <= 1) return 1;
  return Math.floor(Math.log10(abs) + 1);
};

/**
 * Return number of thousands groups in whole part of number
 * E.g. 100 -> 1, 1.000 -> 2, 100.000 -> 2, 1.000.000 -> 3
 */
const getNumThousandGroups = (n: number) => {
  const wholeDigits = getNumWholeDigits(n);
  if (wholeDigits <= 3) return 1;
  return Math.ceil(wholeDigits / 3);
};

// https://stackoverflow.com/questions/46136440/determine-number-of-leading-zeros-in-a-floating-point-number
const getFractionalLeadingZeroes = (n: number) =>
  n % 1 === 0 ? 0 : -1 - Math.floor(Math.log10(n % 1));

const nicelyRoundDown = (n: number) => {
  const numDigits = getNumWholeDigits(n);
  if (Math.abs(n) < 1) {
    // Round to the first non-zero fractional digit
    const numFractionalLeadingZeroes = getFractionalLeadingZeroes(n);
    const multiplier = Math.pow(10, numFractionalLeadingZeroes + 1);
    return Math.floor(n * multiplier) / multiplier;
  } else if (numDigits < 3) {
    return Math.floor(n);
  } else if (numDigits === 3) {
    return Math.floor(n / 10) * 10;
  }
  // Round to the highest-order thousands group
  const numThouGroups = getNumThousandGroups(n);
  const digitsToDrop = (numThouGroups - 1) * 3;
  const multiplier = Math.pow(10, digitsToDrop);
  return Math.floor(n / multiplier) * multiplier;
};

const getConstrainedAndRoundedBubbleData = ({
  chartData,
  radiusDomain: [radiusDomainMinimum, radiusDomainMaximum],
  radiusScaler,
  radiusUnscaler,
}: Omit<BubbleRadiusLegendProps, 'locale'>): LegendBubbleData[] => {
  const dataVals = chartData.map(({ radius }) => radius);
  dataVals.sort((a, b) => a - b);

  const maxBubbleRadius = MAX_BUBBLE_RADIUS;

  const [minConstrainedBubbleData, maxConstrainedBubbleData] =
    constrainLegendBubbleRange(
      dataVals,
      radiusScaler,
      radiusUnscaler,
      maxBubbleRadius
    );

  const isZeroRange = radiusDomainMinimum === radiusDomainMaximum;

  const roundedMax = getBubbleData(
    isZeroRange
      ? maxConstrainedBubbleData.dataValue
      : nicelyRoundDown(maxConstrainedBubbleData.dataValue),
    radiusScaler
  );
  if (!minConstrainedBubbleData) return [roundedMax];
  const roundedMin = getBubbleData(
    isZeroRange
      ? minConstrainedBubbleData.dataValue
      : nicelyRoundDown(minConstrainedBubbleData.dataValue),
    radiusScaler
  );

  const areMinAndMaxAdequatelySpaced =
    roundedMax.radius - roundedMin.radius >= MIN_PIXELS_BETWEEN_BUBBLE_RADII;
  if (!areMinAndMaxAdequatelySpaced) return [roundedMax];

  const intermediateBubbleData = getIntermediateRadiiSteps([
    roundedMin.radius,
    roundedMax.radius,
  ]).map(radius =>
    getBubbleData(nicelyRoundDown(radiusUnscaler(radius)), radiusScaler)
  );
  return [roundedMin, ...intermediateBubbleData, roundedMax].reduce<
    LegendBubbleData[]
  >((resultBubbles, data, i, arr) => {
    if (i === 0 || i === arr.length - 1) return resultBubbles.concat(data);
    const previousData = resultBubbles[resultBubbles.length - 1];
    const nextData = arr[i + 1];
    const isAdequatelySpaced =
      data.radius - previousData.radius >= MIN_PIXELS_BETWEEN_BUBBLE_RADII &&
      nextData.radius - data.radius >= MIN_PIXELS_BETWEEN_BUBBLE_RADII;
    return isAdequatelySpaced ? resultBubbles.concat(data) : resultBubbles;
  }, []);
};

const LEGEND_LABEL_FONT = `normal ${LEGEND_TEXT_PX_SIZE}px/normal ${fontFamily.regular}`;
const getLegendLabelWidth = canvasLabelMeasurer(LEGEND_LABEL_FONT);

const getDiameterWithStroke = (radius: number) =>
  2 * (radius + BUBBLE_LEGEND_STROKE_WIDTH);

export const getLegendData = ({
  chartData,
  radiusDomain,
  radiusScaler,
  radiusUnscaler,
  includeZero,
  locale,
}: BubbleRadiusLegendProps) => {
  const legendBubbleData = getConstrainedAndRoundedBubbleData({
    chartData,
    radiusDomain,
    radiusScaler,
    radiusUnscaler,
    includeZero,
  }).map(({ radius, dataValue }) => ({
    radius,
    label: formatNumber(dataValue, 'default', locale),
  }));
  const labelWidths = legendBubbleData.map(({ label }) =>
    getLegendLabelWidth(label)
  );
  const maxLabelWidth = Math.max(...labelWidths);
  const maxRadius = legendBubbleData[legendBubbleData.length - 1].radius;
  const maxBubbleDiameterWithStroke = getDiameterWithStroke(maxRadius);
  const svgHeight =
    maxBubbleDiameterWithStroke + PADDING_TOP_BUBBLES + PADDING_BOTTOM_BUBBLES;
  const svgWidth =
    maxBubbleDiameterWithStroke +
    PADDING_RIGHT_BUBBLES +
    maxLabelWidth +
    DATA_LABEL_LEFT_PADDING;

  return {
    legendBubbleData,
    maxLabelWidth,
    svgHeight,
    svgWidth,
    maxRadius,
    maxBubbleDiameterWithStroke,
  };
};

export const resolveRadiusField = (
  numericFields: FieldData[],
  selectedFieldNameRadius: string | BubbleChartSelectedFieldInfo | null
): RadiusFieldLegendData => {
  const fieldName =
    typeof selectedFieldNameRadius === 'string'
      ? selectedFieldNameRadius
      : (selectedFieldNameRadius?.fieldName ?? '');

  const field = numericFields.find(f => f.name === fieldName);
  return field ? { _id: field._id, label: field.label } : null;
};
